import { Link } from 'react-router-dom';
import { BrandHeader } from './Brand';
import { useI18n } from './i18n';
import SiteFooter from './SiteFooter';
import { trackClick } from './analytics';

export const LEGAL_UPDATED = 'November 1, 2026';
const LEGAL_UPDATED_ES = '1 de noviembre de 2026';

// The company that runs Market Matrix, named in the Terms (Paddle's review asks for it).
export const COMPANY = 'Iron Horse Prides Holdings LLC';
const operatedBy = (lang) => (COMPANY ? (lang === 'es' ? ` Market Matrix es operado por ${COMPANY}.` : ` Market Matrix is operated by ${COMPANY}.`) : '');

function TranslationNote() {
  return (
    <p className="legal-note">
      Esta traducción al español se ofrece para tu comodidad. Si hay alguna diferencia con la versión en inglés,
      prevalece la versión en inglés.
    </p>
  );
}

function LegalPage({ user, eyebrow, title, intro, children }) {
  const { t, lang } = useI18n();
  return (
    <div className="App">
      <BrandHeader>
        <Link to={lang === 'es' ? '/es/pricing' : '/pricing'} className="header-link">{t('Pricing')}</Link>
        {user ? (
          <Link to="/app" className="header-link">{t('Journal')}</Link>
        ) : (
          <>
            <Link to="/login" className="header-link">{t('Log in')}</Link>
            <Link to="/signup" className="nav-button-small" onClick={() => trackClick('header-signup')}>{t('Sign up free')}</Link>
          </>
        )}
      </BrandHeader>

      <div className="page-title">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="subtitle">{lang === 'es' ? `Última actualización: ${LEGAL_UPDATED_ES}` : `Last updated ${LEGAL_UPDATED}`}</p>
      </div>

      <article className="legal">
        <p className="legal-intro">{intro}</p>
        {children}
      </article>

      <SiteFooter />
    </div>
  );
}

export function TermsPage({ user }) {
  const { lang } = useI18n();
  if (lang === 'es') return <TermsPageEs user={user} />;
  return (
    <LegalPage user={user} eyebrow="Terms of Service" title="The rules for using Market Matrix"
      intro="These terms explain what you can expect from Market Matrix and what we expect from you. By creating an account or using the site, you agree to them. If you don't agree, please don't use Market Matrix.">
      <h2>1. What Market Matrix is</h2>
      <p>
        Market Matrix (marketmatrixhq.com) is a trading journal. It helps you record and review your trades, track your
        account against prop firm rules you enter, and build a daily routine. It is a record-keeping and self-review tool.
        {operatedBy('en')} In these terms, "we" and "us" mean Market Matrix and the company that runs it.
      </p>

      <h2>2. Not financial advice</h2>
      <p>
        Nothing on Market Matrix is financial, investment, tax or legal advice, or a recommendation to buy or sell anything.
        Insights, statistics, tips and quotes are generated from your own data or written for general motivation. They
        don't take your full situation into account. Trading futures and other instruments involves substantial risk of
        loss and isn't suitable for everyone. You alone are responsible for your trading decisions and their results.
      </p>

      <h2>3. Prop firm rules and numbers</h2>
      <p>
        Prop firm presets, balances, drawdown levels, consistency figures, payout counts and account statuses are
        estimates based on the trades and settings you provide and on firm rules as we understood them when we last
        checked. Firms change their rules often, imports can be incomplete, and some rules (such as intraday trailing
        drawdown) can't be measured exactly from trade files. <strong>Always confirm your account's status, limits and
        rules directly with your firm.</strong> We aren't responsible for a failed evaluation, a lost account, a denied
        payout or any other outcome based on figures shown in Market Matrix.
      </p>
      <p>
        Market Matrix is not affiliated with, endorsed by or sponsored by Topstep, Apex Trader Funding, MyFundedFutures,
        Lucid Trading, Tradeify, TradeDay, Tradovate, TopstepX, TradingView or any other firm or platform. Their names are
        used only to describe compatibility.
      </p>

      <h2>4. Your account</h2>
      <ul>
        <li>You must be at least 18 years old to use Market Matrix.</li>
        <li>Give accurate sign-up details and keep your password private. You're responsible for activity on your account.</li>
        <li>Tell us through the <Link to="/contact">contact page</Link> if you think someone else has accessed your account.</li>
        <li>You can change your details or delete your account at any time in Settings.</li>
      </ul>

      <h2>5. Your content</h2>
      <p>
        Your trades, notes, journal entries, goals and anything else you add stay yours. You give us permission to store
        and process them only to run Market Matrix for you, for example to show your calendar, statistics and insights.
        We don't sell your content and we don't share it with other members. You can download it or delete it at any time.
      </p>

      <h2>6. Acceptable use</h2>
      <p>Please don't:</p>
      <ul>
        <li>break the law or anyone's rights while using Market Matrix;</li>
        <li>try to access other people's accounts or data, or get around security or rate limits;</li>
        <li>upload malware, or overload, scrape or disrupt the site;</li>
        <li>use Market Matrix to send spam or to harass anyone, including through the contact form or community links;</li>
        <li>resell or copy the service without our written permission.</li>
      </ul>

      <h2>7. Price and changes to the service</h2>
      <p>
        Market Matrix has a Free plan and a paid Pro plan. Every new account gets a free trial of every feature for 14
        days, with no payment details needed. When the trial ends, the account stays on the Free plan unless you choose
        Pro. The Free plan includes the core journal on one account (importing, logging and automatically syncing trades,
        trade charts, the P&amp;L calendar and stats, the prop firm rules panel and the daily routine pages), and it will
        stay free. Pro adds features that cost more to run, such as unlimited accounts, Insights and the full Notebook. Accounts
        you already have are never taken away. The current price
        is shown on the Plans page before you pay.
      </p>
      <p>
        <strong>The free trial.</strong> The trial doesn't ask for payment details, and it never turns into a paid plan by
        itself. You'll only be charged if you choose Pro and agree to pay.
      </p>
      <p>
        <strong>Billing and automatic renewal.</strong> Prices are in US dollars and don't include sales tax, which is added
        at checkout where it applies. Before you pay, the checkout shows the price, how often you'll be charged (every month
        or every year) and that Pro renews automatically until you cancel. By choosing Pro, you authorize us and our payment
        provider to charge your payment method at the start of each period until you cancel. Our order process is conducted by
        our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders, handles sales tax, and
        provides all customer service inquiries and handles returns; its <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">buyer terms</a> also apply. For a
        yearly plan, we'll email you a reminder before each renewal with the price and how to cancel.
      </p>
      <p>
        <strong>Cancelling.</strong> You can cancel online at any time from the Plans page in your account, without
        contacting us. Cancelling stops the next renewal; you keep Pro until the end of the period you already paid for,
        and then your account moves to the Free plan. Your trades, notes and everything you wrote stay in your account; the
        Pro features simply close.
      </p>
      <p>
        <strong>Refunds.</strong> Your first Pro payment comes with a 14-day money-back guarantee. Other payments aren't
        refundable, except as our <Link to="/refunds">Refund Policy</Link> describes or where the law requires a refund.
      </p>
      <p>
        <strong>Price changes.</strong> If we change the price of Pro, we'll email you at least 30 days before the new price
        applies to your next renewal, so you can cancel first if you prefer. We may also add, change or remove features as
        the product develops.
      </p>
      <p>
        <strong>Founding members.</strong> The first 50 people to create a Market Matrix account (not counting our own
        accounts, demo sessions or test accounts) are founding members. When the 50 spots are taken, the offer ends. Each founding member gets 50% off the regular
        price of any Market Matrix subscription plan, including plans we add later, at whatever that plan costs at the
        time, for as long as they keep their account open. The discount stays if they cancel and later subscribe again.
        It doesn't apply to one-time purchases. It is personal to that account and can't be transferred. Deleting the account ends it. Founding members
        never have to pay: like everyone, they're only charged if they choose a paid plan. Founding members may also join
        the Founders Council, a private area of our Discord server, by linking their Discord account. Joining is optional
        and doesn't affect the badge or discount. The council is a place to share ideas and feedback: we read and
        consider it, but we make the final decisions about features, pricing and the direction of Market Matrix, and we
        may change or close the council. Discord's own terms apply there.
      </p>

      <h2>8. Availability and your records</h2>
      <p>
        We work to keep Market Matrix running and your data safe, but we can't promise the site will always be available,
        error-free or that data will never be lost. Keep your own copies of important records. Your broker or firm
        statements, not Market Matrix, are the official record of your trading. You can download your data from Settings
        at any time.
      </p>

      <h2>9. Ending your use</h2>
      <p>
        You can stop using Market Matrix and delete your account whenever you like. We may suspend or close accounts that
        break these terms or put the service or other members at risk. Where reasonable, we'll let you know first and give
        you a chance to download your data.
      </p>

      <h2>10. Disclaimer and limitation of liability</h2>
      <p>
        Market Matrix is provided "as is" and "as available", without warranties of any kind, to the fullest extent the law
        allows. To the fullest extent the law allows, Market Matrix and the people who run it aren't liable for any
        indirect, incidental, special or consequential damages, or for trading losses, lost profits, lost payouts, failed
        evaluations or lost data arising from your use of the site. Where liability can't be excluded, it's limited to the
        amount you paid us in the 12 months before the claim (nothing, if you've only used the Free plan). Some
        places don't allow these limits, so they may not fully apply to you.
      </p>

      <h2>11. Governing law and disputes</h2>
      <p>
        These terms are governed by the laws of the State of New York, United States, and by applicable US federal law,
        without regard to conflict-of-law rules. If you have a problem with Market Matrix, please contact us first through
        the <Link to="/contact">contact page</Link>: most problems can be solved quickly, and we'll try in good faith to
        resolve it with you within 30 days. If we can't, any claim must be brought in the state or federal courts located
        in New York County, New York, and you and we agree to those courts' jurisdiction. Either of us may instead bring an individual claim
        in small claims court where it qualifies. If you live somewhere whose laws give you consumer rights that can't be
        waived, nothing in these terms takes those rights away.
      </p>
      <p>
        If any part of these terms can't be enforced, the rest still applies. If we don't enforce a part of these terms
        right away, we can still enforce it later.
      </p>

      <h2>12. Changes to these terms</h2>
      <p>
        We may update these terms as Market Matrix changes. We'll change the date at the top, and for significant changes
        we'll let members know on the site or by email before they take effect. Continuing to use Market Matrix after a
        change means you accept the updated terms.
      </p>

      <h2>13. Contact</h2>
      <p>Questions about these terms? Reach us through the <Link to="/contact">contact page</Link>.</p>
      <p>See also our <Link to="/privacy">Privacy Policy</Link>.</p>
    </LegalPage>
  );
}

export function PrivacyPage({ user }) {
  const { lang } = useI18n();
  if (lang === 'es') return <PrivacyPageEs user={user} />;
  return (
    <LegalPage user={user} eyebrow="Privacy Policy" title="How we handle your data"
      intro="Your trading journal is personal. This policy explains what Market Matrix collects, why, who helps us run the site, and the choices you have. The short version: we collect what's needed to run your journal, we don't sell your data, we don't show ads, and you can download or delete everything at any time.">
      <h2>1. What we collect</h2>
      <ul>
        <li><strong>Account details:</strong> your name, email address and password. Passwords are stored only as a secure one-way hash, so we can't see them.</li>
        <li><strong>Your journal:</strong> everything you add or import, such as trades (symbols, prices, sizes, times, P&amp;L, fees, account numbers from your files), trading accounts and their rules, tags, trade notes, chart screenshots you add to trades, daily plans and reviews, morning check-ins and evening reflections (including mood, sleep, energy, stress and emotions you choose to record), session notes, routine and checklists, goals, your trading vision and notebook entries.</li>
        <li><strong>Messages:</strong> what you send through the contact form, with the name and email you enter.</li>
        <li><strong>Free guide sign-ups:</strong> if you ask for our free guide, your email address, your language and the page where you signed up.</li>
        <li><strong>Coach MARK:</strong> if you use our AI coach, your conversations with him, the debriefs and action plans he writes, the rules he suggests, your check-ins, your coaching preferences, what he notes about how you trade, and the credits you use.</li>
        <li><strong>Basic activity:</strong> when you signed up and when you last used the site.</li>
        <li><strong>Site analytics:</strong> which pages are visited, how long a visit lasts, how long and how far blog articles are read, the site that sent you (for example Google or Instagram), your country (worked out from your connection, without storing your IP address), the type of device and the language. Visitors are counted with a random id saved in the browser, not by name. For members, we also count which features are used (for example imports, Morning prep or Insights) to learn what's useful.</li>
        <li><strong>Settings:</strong> your email preferences, your language and your time zone (read from your browser), so emails arrive in your language at the right local time.</li>
        <li><strong>Technical data:</strong> like any website, our hosting provider receives your IP address and browser details when you visit, and keeps short-term server logs for security and troubleshooting.</li>
      </ul>
      <p>
        Check-ins about mood, sleep and emotions are optional. Only record what you're comfortable with.
      </p>

      <h2>2. How we use it</h2>
      <ul>
        <li>to run your journal: store your data and show your calendar, statistics, rules panel, insights and goals;</li>
        <li>to keep your account secure, for example to sign you in, limit repeated login attempts, and email you about password resets and changes to your email or password;</li>
        <li>to send the emails you can control in Settings: a weekly recap of your trading (on by default) and, if you turn it on, a weekday morning prep reminder. Every one of these emails has an unsubscribe link;</li>
        <li>if you ask for the free guide: to email it to you, followed by 3 short emails over about a week: two trading tips and an invitation to try the Market Matrix journal. The series stops if you unsubscribe or create a Market Matrix account, and every email has an unsubscribe link;</li>
        <li>to answer messages you send us;</li>
        <li>to understand how people find and use Market Matrix (such as how many visitors and members there are, and which features are used most) and improve it.</li>
      </ul>
      <p>
        We don't sell your data and we don't use it for advertising. The only third-party analytics we use is Google
        Analytics, and only if you accept it (see section 4). Your journal data is never sent to it.
      </p>

      <h2>3. Who helps us run Market Matrix</h2>
      <p>We use a small number of service providers, who process data only to provide their service to us:</p>
      <ul>
        <li><strong>Render</strong> hosts the website and server.</li>
        <li><strong>MongoDB Atlas</strong> stores the database with your account and journal.</li>
        <li><strong>Resend</strong> sends our emails, such as password resets, security notices, recaps and the free guide.</li>
        <li><strong>Discord:</strong> messages you send us through the contact form, the chat bubble or by email to hello@marketmatrixhq.com (your name, email and message) are forwarded to a private Discord channel that only the Market Matrix team can see, so we can reply quickly.</li>
        <li><strong>Google Fonts</strong> serves the site's fonts, so your browser connects to Google to load them.</li>
        <li><strong>Sign in with Google</strong> (optional): if you choose Continue with Google, Google tells us your name, email address and a Google account ID, which we use only to create your account and log you in. We never see your Google password, and we don't get access to anything else in your Google account.</li>
        <li><strong>Cloudflare</strong> manages our domain name, tells us which country a visit comes from, and stores the chart screenshots you add to trades (Cloudflare R2). Screenshots are private: they're only shown to you, through links that expire after an hour, and they're deleted with the trade or your account. Emails sent to hello@marketmatrixhq.com pass through Cloudflare on their way to us.</li>
        <li><strong>Paddle</strong> (only if you choose Pro) is our reseller and Merchant of Record: it takes the payment,
          handles sales tax and sends your receipts. Paddle receives your name, email, country, and your card or other
          payment details, under its own privacy policy. We never see or store your card number; we only learn your plan,
          when it renews and whether a payment went through.</li>
        <li><strong>Google Analytics</strong> (only if you accept analytics cookies) measures visits: pages viewed, how you arrived, device and approximate location. It never receives your journal data, name or email.</li>
        <li><strong>Anthropic</strong> (only if you use Coach MARK or import trades from screenshots or PDFs) runs the AI behind these features (Claude). When you ask MARK something or get a debrief, we send Anthropic what he needs to answer: your trades and results for the accounts you picked, your notes and reflections, your rules, your coaching preferences and what MARK knows about you. When you import from screenshots or PDFs, we send the files you choose. Anthropic uses this only to produce the answer, under its commercial terms, which don't allow it to use your data to train its models. We ask you to agree before MARK sends anything.</li>
      </ul>
      <p>
        These providers may store data in the United States. We may also share information if the law requires it, to
        protect people's safety or our rights, or as part of a sale or transfer of Market Matrix (in which case this
        policy would continue to apply to your data).
      </p>
      <p>
        Links to other sites, such as our Discord community, are covered by those sites' own privacy policies.
      </p>

      <h2>4. Cookies and browser storage</h2>
      <p>
        We use one essential cookie, <code>mm_session</code>, to keep you logged in. It lasts up to 30 days and can't be
        read by scripts on the page. We also save a few preferences in your browser's local storage, such as your language, selected
        account, chosen Insights period, table sorting, a running timeout timer and banners you've dismissed. These stay
        on your device, along with a random visitor id and visit id that our own analytics uses to count visits
        without knowing who you are.
      </p>
      <p>
        If you accept analytics cookies in the banner, Google Analytics sets its own cookies (such as <code>_ga</code>)
        to measure visits. The chat bubble is part of Market Matrix itself and sets no cookies: once you send a
        message, your browser keeps a private key for your conversation (in its local storage) so only you can see our replies. If you decline, Google Analytics doesn't load and the site works exactly the same. To change
        your choice, clear this site's data in your browser and the banner will ask again. We don't use advertising
        cookies.
      </p>

      <h2>5. Your choices and rights</h2>
      <ul>
        <li><strong>See and download your data:</strong> in Settings you can download your trades as a CSV and everything in your account as a file.</li>
        <li><strong>Correct it:</strong> edit trades, notes and other entries directly, and update your name and email in Settings.</li>
        <li><strong>Delete it:</strong> delete individual entries, clear an account's trades, or delete your whole account in Settings. Deleting your account removes your data from our live database immediately.</li>
        <li><strong>Ask us:</strong> for anything else, including questions about your data, use the <Link to="/contact">contact page</Link>. Depending on where you live, you may have additional rights under laws like the GDPR or CCPA, and we'll honor those requests.</li>
      </ul>

      <h2>6. How long we keep data</h2>
      <p>
        We keep your data while your account is open. When you delete something, or your whole account, it's removed from
        our live database straight away. Copies may remain in our providers' routine backups or logs for a limited time
        before they're overwritten. Contact messages are kept until we've dealt with them or you delete your account. Free guide sign-ups are kept until you ask us to remove them; unsubscribing stops every email in the series. Analytics records are deleted automatically after about 13 months, and the feature-use records linked to your account are deleted with it.
      </p>

      <h2>7. Security</h2>
      <p>
        Connections to Market Matrix are encrypted (HTTPS), passwords are hashed, sessions use a secure cookie, and login
        attempts are rate-limited. Each member can only access their own data. No system is perfectly secure, so please
        use a strong, unique password. If we ever learn of a breach affecting your data, we'll let you know as the law
        requires.
      </p>

      <h2>8. Children</h2>
      <p>Market Matrix is for adults 18 and over. We don't knowingly collect data from anyone younger. If you believe a minor has an account, contact us and we'll delete it.</p>

      <h2>9. Changes to this policy</h2>
      <p>
        If we change how we handle your data, we'll update this page and the date at the top, and tell members about
        significant changes on the site or by email.
      </p>

      <h2>10. Contact</h2>
      <p>Questions about privacy or your data? Reach us through the <Link to="/contact">contact page</Link>.</p>
      <p>See also our <Link to="/terms">Terms of Service</Link>.</p>
    </LegalPage>
  );
}

function TermsPageEs({ user }) {
  return (
    <LegalPage user={user} eyebrow="Términos del servicio" title="Las reglas para usar Market Matrix"
      intro="Estos términos explican qué puedes esperar de Market Matrix y qué esperamos de ti. Al crear una cuenta o usar el sitio, los aceptas. Si no estás de acuerdo, por favor no uses Market Matrix.">
      <TranslationNote />
      <h2>1. Qué es Market Matrix</h2>
      <p>
        Market Matrix (marketmatrixhq.com) es un diario de trading. Te ayuda a registrar y revisar tus operaciones,
        seguir tu cuenta frente a las reglas de la empresa de fondeo que ingreses y construir una rutina diaria. Es una
        herramienta de registro y autoevaluación.{operatedBy('es')} En estos términos, "nosotros" significa Market Matrix y la
        empresa que lo opera.
      </p>

      <h2>2. No es asesoría financiera</h2>
      <p>
        Nada en Market Matrix es asesoría financiera, de inversión, fiscal ni legal, ni una recomendación para comprar o
        vender algo. Los análisis, estadísticas, consejos y frases se generan a partir de tus propios datos o se escriben
        como motivación general. No consideran tu situación completa. Operar futuros y otros instrumentos implica un
        riesgo considerable de pérdida y no es adecuado para todos. Solo tú eres responsable de tus decisiones de trading
        y de sus resultados.
      </p>

      <h2>3. Reglas y cifras de las empresas de fondeo</h2>
      <p>
        Las plantillas de empresas de fondeo, saldos, niveles de pérdida máxima, cifras de consistencia, conteos para
        retiros y estados de cuenta son estimaciones basadas en las operaciones y configuraciones que proporcionas y en
        las reglas de cada empresa tal como las entendimos la última vez que las revisamos. Las empresas cambian sus
        reglas con frecuencia, las importaciones pueden estar incompletas y algunas reglas (como la pérdida máxima móvil
        intradía) no se pueden medir con exactitud a partir de los archivos de operaciones. <strong>Confirma siempre el
        estado, los límites y las reglas de tu cuenta directamente con tu empresa.</strong> No somos responsables de una
        evaluación reprobada, una cuenta perdida, un retiro negado ni ningún otro resultado basado en las cifras que
        muestra Market Matrix.
      </p>
      <p>
        Market Matrix no está afiliado, respaldado ni patrocinado por Topstep, Apex Trader Funding, MyFundedFutures,
        Lucid Trading, Tradeify, TradeDay, Tradovate, TopstepX, TradingView ni ninguna otra empresa o plataforma. Sus
        nombres se usan solo para describir la compatibilidad.
      </p>

      <h2>4. Tu cuenta</h2>
      <ul>
        <li>Debes tener al menos 18 años para usar Market Matrix.</li>
        <li>Proporciona datos de registro correctos y mantén tu contraseña en privado. Eres responsable de la actividad en tu cuenta.</li>
        <li>Avísanos en la <Link to="/contact">página de contacto</Link> si crees que alguien más ha entrado a tu cuenta.</li>
        <li>Puedes cambiar tus datos o eliminar tu cuenta en cualquier momento desde Configuración.</li>
      </ul>

      <h2>5. Tu contenido</h2>
      <p>
        Tus operaciones, notas, entradas del diario, metas y todo lo que agregues siguen siendo tuyos. Nos das permiso
        para guardarlos y procesarlos solo para que Market Matrix funcione para ti, por ejemplo para mostrar tu
        calendario, estadísticas y análisis. No vendemos tu contenido ni lo compartimos con otros miembros. Puedes
        descargarlo o eliminarlo cuando quieras.
      </p>

      <h2>6. Uso aceptable</h2>
      <p>Por favor, no:</p>
      <ul>
        <li>infrinjas la ley ni los derechos de nadie al usar Market Matrix;</li>
        <li>intentes acceder a cuentas o datos de otras personas, ni evadir la seguridad o los límites de uso;</li>
        <li>subas software malicioso ni sobrecargues, copies masivamente o interrumpas el sitio;</li>
        <li>uses Market Matrix para enviar spam o acosar a alguien, incluso mediante el formulario de contacto o los enlaces de la comunidad;</li>
        <li>revendas o copies el servicio sin nuestro permiso por escrito.</li>
      </ul>

      <h2>7. Precio y cambios en el servicio</h2>
      <p>
        Market Matrix tiene un plan Gratis y un plan de pago, Pro. Cada cuenta nueva tiene una prueba gratis de todas las
        funciones por 14 días, sin pedir datos de pago. Cuando termina la prueba, la cuenta queda en el plan Gratis a menos
        que elijas Pro. El plan Gratis incluye el diario básico en una cuenta (importar, registrar y sincronizar
        automáticamente operaciones, las gráficas de tus operaciones, el calendario de P&amp;L y las cifras, el panel de
        reglas de la empresa de fondeo y las páginas de la rutina diaria), y seguirá siendo gratis. Pro agrega funciones que
        cuestan más de mantener, como cuentas ilimitadas, Análisis y el cuaderno completo. Las cuentas que ya tienes nunca
        se te quitan. El precio vigente aparece en la página de
        Planes antes de pagar.
      </p>
      <p>
        <strong>La prueba gratis.</strong> La prueba no pide datos de pago y nunca se convierte sola en un plan de pago.
        Solo se te cobra si eliges Pro y aceptas pagar.
      </p>
      <p>
        <strong>Cobro y renovación automática.</strong> Los precios están en dólares estadounidenses y no incluyen el
        impuesto sobre las ventas, que se agrega al pagar donde aplique. Antes de pagar, la página de pago muestra el
        precio, cada cuánto se cobra (cada mes o cada año) y que Pro se renueva automáticamente hasta que lo canceles. Al
        elegir Pro, nos autorizas a nosotros y a nuestro proveedor de pagos a cobrar tu método de pago al inicio de cada
        periodo hasta que canceles. El proceso de compra lo realiza nuestro revendedor en línea
        Paddle.com. Paddle.com es el Comerciante Registrado (Merchant of Record) de todos nuestros pedidos, se encarga de
        los impuestos sobre las ventas, atiende las consultas sobre pagos y gestiona las devoluciones; sus <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">condiciones para compradores</a> también aplican. En el plan anual, te enviaremos un correo antes de
        cada renovación con el precio y cómo cancelar.
      </p>
      <p>
        <strong>Cancelar.</strong> Puedes cancelar en línea en cualquier momento desde la página de Planes de tu cuenta,
        sin tener que contactarnos. Al cancelar se detiene la siguiente renovación; conservas Pro hasta el final del
        periodo que ya pagaste y después tu cuenta pasa al plan Gratis. Tus operaciones, notas y todo lo que escribiste
        se quedan en tu cuenta; solo se cierran las funciones Pro.
      </p>
      <p>
        <strong>Reembolsos.</strong> Tu primer pago de Pro tiene una garantía de devolución de 14 días. Los demás pagos no
        son reembolsables, salvo lo que describe nuestra <Link to="/refunds">Política de reembolsos</Link> o cuando la ley
        exija un reembolso.
      </p>
      <p>
        <strong>Cambios de precio.</strong> Si cambiamos el precio de Pro, te escribiremos al menos 30 días antes de que el
        nuevo precio se aplique a tu siguiente renovación, para que puedas cancelar antes si lo prefieres. También podemos
        agregar, cambiar o quitar funciones a medida que el producto evoluciona.
      </p>
      <p>
        <strong>Miembros fundadores.</strong> Las primeras 50 personas que crean una cuenta en Market Matrix (sin contar
        nuestras propias cuentas, las sesiones de demostración ni las cuentas de prueba) son miembros fundadores y tienen
        un 50% de descuento en el precio normal de cualquier plan de suscripción de Market Matrix, incluidos los planes
        que agreguemos después, al precio que tenga ese plan en el momento, mientras su cuenta siga abierta. El descuento
        se mantiene si cancelan y después vuelven a suscribirse. No aplica a compras únicas. Cuando se ocupan los 50 lugares,
        la oferta termina. El descuento es personal, no se puede transferir y termina si se elimina la cuenta.
      </p>

      <h2>8. Disponibilidad y tus registros</h2>
      <p>
        Trabajamos para que Market Matrix funcione y tus datos estén seguros, pero no podemos garantizar que el sitio
        esté siempre disponible, sin errores, ni que nunca se pierdan datos. Guarda tus propias copias de los registros
        importantes. Los estados de cuenta de tu bróker o empresa, no Market Matrix, son el registro oficial de tu
        trading. Puedes descargar tus datos desde Configuración en cualquier momento.
      </p>

      <h2>9. Dejar de usar el servicio</h2>
      <p>
        Puedes dejar de usar Market Matrix y eliminar tu cuenta cuando quieras. Podemos suspender o cerrar cuentas que
        incumplan estos términos o pongan en riesgo el servicio o a otros miembros. Cuando sea razonable, te avisaremos
        antes y te daremos la oportunidad de descargar tus datos.
      </p>

      <h2>10. Exención y limitación de responsabilidad</h2>
      <p>
        Market Matrix se ofrece "tal cual" y "según disponibilidad", sin garantías de ningún tipo, en la medida máxima
        que permita la ley. En la medida máxima que permita la ley, Market Matrix y las personas que lo administran no son
        responsables de daños indirectos, incidentales, especiales o consecuentes, ni de pérdidas de trading, ganancias
        perdidas, retiros perdidos, evaluaciones reprobadas o datos perdidos que surjan del uso del sitio. Cuando la
        responsabilidad no pueda excluirse, se limita a la cantidad que nos hayas pagado en los 12 meses anteriores al
        reclamo (cero, si solo has usado el plan Gratis). Algunos lugares no permiten estas limitaciones, por lo
        que podrían no aplicarse por completo en tu caso.
      </p>

      <h2>11. Ley aplicable y disputas</h2>
      <p>
        Estos términos se rigen por las leyes del Estado de Nueva York, Estados Unidos, y por la ley federal de Estados Unidos
        que aplique, sin tomar en cuenta las normas sobre conflicto de leyes. Si tienes un problema con Market Matrix,
        escríbenos primero desde la <Link to="/contact">página de contacto</Link>: la mayoría de los problemas se
        resuelven rápido, y trataremos de buena fe de resolverlo contigo dentro de 30 días. Si no podemos, cualquier
        reclamo debe presentarse ante los tribunales estatales o federales ubicados en el condado de Nueva York, Nueva York, y tú y nosotros aceptamos
        su jurisdicción. Cualquiera de los dos puede, en cambio, presentar un reclamo individual ante un tribunal de
        reclamos menores cuando califique. Si vives en un lugar cuyas leyes te dan derechos como consumidor que no se
        pueden renunciar, nada en estos términos te los quita.
      </p>
      <p>
        Si alguna parte de estos términos no se puede hacer cumplir, el resto sigue aplicando. Si no hacemos cumplir una
        parte de estos términos de inmediato, todavía podemos hacerlo después.
      </p>

      <h2>12. Cambios a estos términos</h2>
      <p>
        Podemos actualizar estos términos a medida que Market Matrix cambie. Cambiaremos la fecha de arriba y, si los
        cambios son importantes, avisaremos a los miembros en el sitio o por correo antes de que entren en vigor. Seguir
        usando Market Matrix después de un cambio significa que aceptas los términos actualizados.
      </p>

      <h2>13. Contacto</h2>
      <p>¿Preguntas sobre estos términos? Escríbenos desde la <Link to="/contact">página de contacto</Link>.</p>
      <p>Consulta también nuestra <Link to="/privacy">Política de privacidad</Link>.</p>
    </LegalPage>
  );
}

function PrivacyPageEs({ user }) {
  return (
    <LegalPage user={user} eyebrow="Política de privacidad" title="Cómo tratamos tus datos"
      intro="Tu diario de trading es personal. Esta política explica qué recopila Market Matrix, por qué, quién nos ayuda a operar el sitio y qué opciones tienes. En resumen: recopilamos lo necesario para que tu diario funcione, no vendemos tus datos, no mostramos anuncios y puedes descargar o eliminar todo en cualquier momento.">
      <TranslationNote />
      <h2>1. Qué recopilamos</h2>
      <ul>
        <li><strong>Datos de la cuenta:</strong> tu nombre, correo electrónico y contraseña. Las contraseñas se guardan solo como un código cifrado de un solo sentido, así que no podemos verlas.</li>
        <li><strong>Tu diario:</strong> todo lo que agregas o importas, como operaciones (símbolos, precios, tamaños, horarios, resultados, comisiones y números de cuenta de tus archivos), cuentas de trading y sus reglas, etiquetas, notas de operaciones, capturas de gráficos que agregues a tus operaciones, planes y revisiones diarias, registros emocionales de la mañana y reflexiones de cierre (incluidos el ánimo, el sueño, la energía, el estrés y las emociones que decidas anotar), notas de sesión, rutina y listas de verificación, metas, tu visión de trading, entradas del cuaderno, tu manual de estrategias y las calificaciones de tus operaciones.</li>
        <li><strong>Mensajes:</strong> lo que envías por el formulario de contacto, con el nombre y el correo que escribas.</li>
        <li><strong>Registros para la guía gratis:</strong> si pides nuestra guía gratis, tu correo electrónico, tu idioma y la página donde te registraste.</li>
        <li><strong>Coach MARK:</strong> si usas nuestro coach con IA, tus conversaciones con él, los resúmenes y planes de acción que escribe, las reglas que sugiere, tus registros diarios del plan, tus preferencias de coaching, lo que anota sobre cómo operas y los créditos que usas.</li>
        <li><strong>Actividad básica:</strong> cuándo te registraste y cuándo usaste el sitio por última vez.</li>
        <li><strong>Analítica del sitio:</strong> qué páginas se visitan, cuánto dura una visita, cuánto tiempo y hasta dónde se leen los artículos del blog, el sitio que te trajo (por ejemplo Google o Instagram), tu país (calculado a partir de tu conexión, sin guardar tu dirección IP), el tipo de dispositivo y el idioma. Contamos a los visitantes con un identificador aleatorio guardado en el navegador, no por su nombre. En el caso de los miembros, también contamos qué funciones se usan (por ejemplo importaciones, Preparación matutina o Análisis) para saber qué es útil.</li>
        <li><strong>Configuración:</strong> tus preferencias de correo, tu idioma y tu zona horaria (tomada de tu navegador), para que los correos lleguen a la hora local correcta.</li>
        <li><strong>Datos técnicos:</strong> como cualquier sitio web, nuestro proveedor de alojamiento recibe tu dirección IP y los datos de tu navegador cuando nos visitas, y guarda registros temporales del servidor por seguridad y para resolver problemas.</li>
      </ul>
      <p>Los registros de ánimo, sueño y emociones son opcionales. Anota solo lo que te resulte cómodo.</p>

      <h2>2. Cómo lo usamos</h2>
      <ul>
        <li>para que tu diario funcione: guardar tus datos y mostrar tu calendario, estadísticas, panel de reglas, análisis y metas;</li>
        <li>para mantener tu cuenta segura, por ejemplo para iniciar tu sesión, limitar intentos repetidos de acceso y enviarte correos sobre el restablecimiento de contraseña y cambios en tu correo o contraseña;</li>
        <li>para enviarte los correos que controlas en Configuración: un resumen semanal de tu trading (activado por defecto) y, si lo activas, un recordatorio de preparación matutina entre semana. Cada uno incluye un enlace para darte de baja;</li>
        <li>si pides la guía gratis: para enviártela por correo, seguida de 3 correos cortos durante una semana aproximadamente: dos consejos de trading y una invitación a probar el diario de Market Matrix. La serie se detiene si te das de baja o creas una cuenta en Market Matrix, y cada correo incluye un enlace para darte de baja;</li>
        <li>para responder los mensajes que nos envías;</li>
        <li>para entender cómo las personas encuentran y usan Market Matrix (como cuántos visitantes y miembros hay, y qué funciones se usan más) y mejorarlo.</li>
      </ul>
      <p>
        No vendemos tus datos y no los usamos para publicidad. La única analítica de terceros que usamos es Google
        Analytics, y solo si la aceptas (consulta la sección 4). Nunca recibe los datos de tu diario.
      </p>

      <h2>3. Quién nos ayuda a operar Market Matrix</h2>
      <p>Usamos pocos proveedores de servicios, que procesan los datos solo para prestarnos su servicio:</p>
      <ul>
        <li><strong>Render</strong> aloja el sitio web y el servidor.</li>
        <li><strong>MongoDB Atlas</strong> guarda la base de datos con tu cuenta y tu diario.</li>
        <li><strong>Resend</strong> envía nuestros correos, como restablecimientos de contraseña, avisos de seguridad, resúmenes y la guía gratis.</li>
        <li><strong>Discord:</strong> los mensajes que nos envías por el formulario de contacto, la burbuja de chat o por correo a hello@marketmatrixhq.com (tu nombre, correo y mensaje) se envían a un canal privado de Discord que solo ve el equipo de Market Matrix, para poder responder rápido.</li>
        <li><strong>Google Fonts</strong> proporciona las tipografías del sitio, por lo que tu navegador se conecta a Google para cargarlas.</li>
        <li><strong>Iniciar sesión con Google</strong> (opcional): si eliges Continuar con Google, Google nos comparte tu nombre, tu correo y un identificador de tu cuenta de Google, que usamos solo para crear tu cuenta e iniciar tu sesión. Nunca vemos tu contraseña de Google y no tenemos acceso a nada más de tu cuenta de Google.</li>
        <li><strong>Cloudflare</strong> administra nuestro nombre de dominio, nos indica de qué país viene una visita y guarda las capturas de gráficos que agregas a tus operaciones (Cloudflare R2). Las capturas son privadas: solo se te muestran a ti, mediante enlaces que vencen después de una hora, y se borran junto con la operación o tu cuenta. Los correos enviados a hello@marketmatrixhq.com pasan por Cloudflare antes de llegarnos.</li>
        <li><strong>Paddle</strong> (solo si eliges Pro) es nuestro revendedor y Comerciante Registrado: cobra el pago, se
          encarga de los impuestos sobre las ventas y te envía los recibos. Paddle recibe tu nombre, tu correo, tu país y los
          datos de tu tarjeta u otro método de pago, bajo su propia política de privacidad. Nunca vemos ni guardamos el
          número de tu tarjeta; solo sabemos tu plan, cuándo se renueva y si un pago se completó.</li>
        <li><strong>Google Analytics</strong> (solo si aceptas las cookies de analítica) mide las visitas: páginas vistas, cómo llegaste, dispositivo y ubicación aproximada. Nunca recibe los datos de tu diario, tu nombre ni tu correo.</li>
        <li><strong>Anthropic</strong> (solo si usas Coach MARK o importas operaciones desde capturas de pantalla o PDF) opera la inteligencia artificial de estas funciones (Claude). Cuando le preguntas algo a MARK o pides un resumen, le enviamos a Anthropic lo que necesita para responder: tus operaciones y resultados de las cuentas que elegiste, tus notas y reflexiones, tus reglas, tus preferencias de coaching y lo que MARK sabe de ti. Cuando importas desde capturas o PDF, enviamos los archivos que eliges. Anthropic usa esto solo para dar la respuesta, bajo sus términos comerciales, que no le permiten usar tus datos para entrenar sus modelos. Te pedimos tu aceptación antes de que MARK envíe cualquier dato.</li>
      </ul>
      <p>
        Estos proveedores pueden guardar datos en Estados Unidos. También podemos compartir información si la ley lo
        exige, para proteger la seguridad de las personas o nuestros derechos, o como parte de una venta o transferencia
        de Market Matrix (en cuyo caso esta política seguiría aplicándose a tus datos).
      </p>
      <p>Los enlaces a otros sitios, como nuestra comunidad de Discord, se rigen por las políticas de privacidad de esos sitios.</p>

      <h2>4. Cookies y almacenamiento del navegador</h2>
      <p>
        Usamos una cookie esencial, <code>mm_session</code>, para mantener tu sesión iniciada. Dura hasta 30 días y los
        scripts de la página no pueden leerla. También guardamos algunas preferencias en el almacenamiento local de tu
        navegador, como tu idioma, la cuenta seleccionada, el periodo elegido en Análisis, el orden de las tablas, un
        temporizador de pausa en curso y los avisos que cerraste. Estas se quedan en tu dispositivo, junto con un
        identificador aleatorio de visitante y de visita que nuestra analítica usa para contar visitas sin saber quién eres.
      </p>
      <p>
        Si aceptas las cookies de analítica en el aviso, Google Analytics guarda sus propias cookies (como <code>_ga</code>)
        para medir las visitas. La burbuja de chat es parte de Market Matrix y no usa cookies: cuando envías un mensaje,
        tu navegador guarda una clave privada de tu conversación (en su almacenamiento local) para que solo tú veas nuestras respuestas. Si las rechazas, Google Analytics no se carga y el sitio funciona exactamente igual. Para
        cambiar tu decisión, borra los datos de este sitio en tu navegador y el aviso volverá a preguntarte. No usamos
        cookies de publicidad.
      </p>

      <h2>5. Tus opciones y derechos</h2>
      <ul>
        <li><strong>Ver y descargar tus datos:</strong> en Configuración puedes descargar tus operaciones en CSV y todo el contenido de tu cuenta en un archivo.</li>
        <li><strong>Corregirlos:</strong> edita operaciones, notas y otras entradas directamente, y actualiza tu nombre y correo en Configuración.</li>
        <li><strong>Eliminarlos:</strong> elimina entradas individuales, borra las operaciones de una cuenta o elimina tu cuenta completa en Configuración. Al eliminar tu cuenta, tus datos se borran de inmediato de nuestra base de datos activa.</li>
        <li><strong>Escríbenos:</strong> para cualquier otra cosa, incluidas preguntas sobre tus datos, usa la <Link to="/contact">página de contacto</Link>. Según dónde vivas, puedes tener derechos adicionales bajo leyes como el RGPD o la CCPA, y atenderemos esas solicitudes.</li>
      </ul>

      <h2>6. Cuánto tiempo guardamos los datos</h2>
      <p>
        Guardamos tus datos mientras tu cuenta esté abierta. Cuando eliminas algo, o tu cuenta completa, se borra de
        inmediato de nuestra base de datos activa. Pueden quedar copias en los respaldos o registros habituales de
        nuestros proveedores durante un tiempo limitado antes de sobrescribirse. Los mensajes de contacto se guardan
        hasta que los atendamos o hasta que elimines tu cuenta. Los registros para la guía gratis se guardan hasta que nos pidas
        borrarlos; al darte de baja se detienen todos los correos de la serie. Los registros de analítica se borran automáticamente después
        de unos 13 meses, y los registros de uso de funciones vinculados a tu cuenta se borran junto con ella.
      </p>

      <h2>7. Seguridad</h2>
      <p>
        Las conexiones con Market Matrix están cifradas (HTTPS), las contraseñas se guardan cifradas, las sesiones usan
        una cookie segura y los intentos de inicio de sesión tienen un límite. Cada miembro solo puede acceder a sus
        propios datos. Ningún sistema es perfectamente seguro, así que usa una contraseña fuerte y única. Si alguna vez
        detectamos una filtración que afecte tus datos, te avisaremos como lo exija la ley.
      </p>

      <h2>8. Menores de edad</h2>
      <p>Market Matrix es para adultos mayores de 18 años. No recopilamos a sabiendas datos de menores. Si crees que un menor tiene una cuenta, contáctanos y la eliminaremos.</p>

      <h2>9. Cambios a esta política</h2>
      <p>
        Si cambiamos la forma en que tratamos tus datos, actualizaremos esta página y la fecha de arriba, y avisaremos a
        los miembros de los cambios importantes en el sitio o por correo.
      </p>

      <h2>10. Contacto</h2>
      <p>¿Preguntas sobre la privacidad o tus datos? Escríbenos desde la <Link to="/contact">página de contacto</Link>.</p>
      <p>Consulta también nuestros <Link to="/terms">Términos del servicio</Link>.</p>
    </LegalPage>
  );
}

// The Refund Policy at /refunds (Paddle's review asks for one, linked from the site's menus).
export function RefundPage({ user }) {
  const { lang } = useI18n();
  if (lang === 'es') return <RefundPageEs user={user} />;
  return (
    <LegalPage user={user} eyebrow="Refund Policy" title="Refunds and cancelling"
      intro="We want Pro to be worth it. If it isn't, here's how to get your money back and how to cancel.">
      <h2>1. 14-day money-back guarantee</h2>
      <p>
        If Pro isn't right for you, ask within 14 days of your first Pro payment, monthly or yearly, and we'll refund it in
        full. No questions asked. This applies to the first payment on your account.
      </p>

      <h2>2. Free trial and the Free plan</h2>
      <p>
        The 14-day free trial of every feature doesn't ask for payment details, so there's nothing to refund. The Free plan
        is never charged.
      </p>

      <h2>3. Renewals</h2>
      <p>
        After the first payment, monthly renewals aren't refundable, but you can cancel at any time and keep Pro until the
        end of the period you paid for. If a yearly plan renews and you meant to cancel, contact us within 14 days of the
        renewal and we'll refund it.
      </p>

      <h2>4. Mistakes</h2>
      <p>If you were charged twice, charged after you cancelled, or charged the wrong amount, we'll refund the difference.</p>

      <h2>5. How to ask for a refund</h2>
      <p>
        Write to us through the <Link to="/contact">contact page</Link> or at hello@marketmatrixhq.com from the email on
        your account, or reply to your Paddle receipt. Our payments are handled by Paddle.com, our reseller and Merchant of
        Record, so the refund goes back to the payment method you used, usually within 5 to 10 business days.
      </p>

      <h2>6. How to cancel</h2>
      <p>
        Cancel online at any time from the Plans page in your account. Cancelling stops the next renewal. Your trades, notes
        and everything you wrote stay in your account, and the core journal keeps working for free. Deleting your account
        also cancels Pro.
      </p>

      <h2>7. Your rights under the law</h2>
      <p>
        If the law where you live gives you more rights, such as the 14-day right to cancel for consumers in the European
        Union and the United Kingdom, those rights apply as well. See also our <Link to="/terms">Terms of Service</Link>.
      </p>
    </LegalPage>
  );
}

function RefundPageEs({ user }) {
  return (
    <LegalPage user={user} eyebrow="Política de reembolsos" title="Reembolsos y cancelación"
      intro="Queremos que Pro valga la pena. Si no es así, aquí te explicamos cómo recuperar tu dinero y cómo cancelar.">
      <TranslationNote />
      <h2>1. Garantía de devolución de 14 días</h2>
      <p>
        Si Pro no es para ti, pídelo dentro de los 14 días posteriores a tu primer pago de Pro, mensual o anual, y te lo
        reembolsamos completo. Sin preguntas. Aplica al primer pago de tu cuenta.
      </p>

      <h2>2. Prueba gratis y plan Gratis</h2>
      <p>
        La prueba gratis de 14 días de todas las funciones no pide datos de pago, así que no hay nada que reembolsar. El
        plan Gratis nunca se cobra.
      </p>

      <h2>3. Renovaciones</h2>
      <p>
        Después del primer pago, las renovaciones mensuales no son reembolsables, pero puedes cancelar en cualquier momento
        y conservas Pro hasta el final del periodo que pagaste. Si tu plan anual se renueva y querías cancelarlo,
        contáctanos dentro de los 14 días posteriores a la renovación y te lo reembolsamos.
      </p>

      <h2>4. Errores</h2>
      <p>Si se te cobró dos veces, después de cancelar o un monto equivocado, te reembolsamos la diferencia.</p>

      <h2>5. Cómo pedir un reembolso</h2>
      <p>
        Escríbenos desde la <Link to="/contact">página de contacto</Link> o a hello@marketmatrixhq.com desde el correo de tu
        cuenta, o responde a tu recibo de Paddle. Nuestros pagos los gestiona Paddle.com, nuestro revendedor y Comerciante
        Registrado, así que el reembolso regresa al método de pago que usaste, normalmente en 5 a 10 días hábiles.
      </p>

      <h2>6. Cómo cancelar</h2>
      <p>
        Cancela en línea en cualquier momento desde la página de Planes de tu cuenta. Al cancelar se detiene la siguiente
        renovación. Tus operaciones, notas y todo lo que escribiste se quedan en tu cuenta, y el diario básico sigue
        funcionando gratis. Eliminar tu cuenta también cancela Pro.
      </p>

      <h2>7. Tus derechos según la ley</h2>
      <p>
        Si la ley de donde vives te da más derechos, como el derecho de cancelación de 14 días para consumidores de la
        Unión Europea y el Reino Unido, esos derechos también aplican. Consulta también nuestros <Link to="/terms">Términos del servicio</Link>.
      </p>
    </LegalPage>
  );
}
